import {
  AutoComplete as AntdAutoComplete,
  Checkbox as AntdCheckbox,
  DatePicker as AntdDefaultDatePicker,
  Form as AntdForm,
  Input as AntdInput,
  InputNumber as AntdInputNumber,
  InputRef as AntdInputRef,
  Radio as AntdRadio,
  Select as AntdSelect,
  SelectProps as AntdSelectProps,
  TreeSelect as AntdTreeSelect,
  AutoCompleteProps,
  CheckboxProps,
  FormInstance,
  FormItemProps,
  FormProps,
  InputNumberProps,
  InputProps,
  RadioProps,
  TreeSelectProps
} from 'antd';
import { CheckboxGroupProps } from 'antd/es/checkbox';
import type { PickerProps } from 'antd/es/date-picker/generatePicker';
import { ErrorListProps, FormListProps } from 'antd/es/form';
import { FormListFieldData, FormListOperation } from 'antd/es/form/FormList';
import { TextAreaProps } from 'antd/es/input';
import { TextAreaRef } from 'antd/es/input/TextArea';
import { LabeledValue } from 'antd/es/select';
import dateFnsGenerateConfig from 'rc-picker/lib/generate/dateFns';
import React, { MutableRefObject, ReactElement, useContext, useEffect, useRef } from 'react';

import { NoInfer, dateFormat, useTranslation } from '@threatadvisor/react-utils';
import generatePicker from 'antd/es/date-picker/generatePicker';
import { ParseError, parse as parseJson, printParseErrorCode } from 'jsonc-parser';

import en from 'antd/es/date-picker/locale/en_US';
// For most components in this collection, we are just adding support for ReadOnly context

export const ReadOnlyContext = React.createContext<boolean>(false);

export const Input = React.forwardRef<AntdInputRef, InputProps>(
  (props: InputProps, ref: React.ForwardedRef<AntdInputRef>): ReactElement => {
    const readOnly = useContext(ReadOnlyContext);
    return <AntdInput {...props} readOnly={readOnly ?? props.readOnly} ref={ref} />;
  }
);

export const TextArea = React.forwardRef(
  (props: TextAreaProps, ref: React.ForwardedRef<TextAreaRef>): ReactElement => {
    const readOnly = useContext(ReadOnlyContext);
    return (
      <AntdInput.TextArea
        {...props}
        readOnly={readOnly ?? props.readOnly}
        ref={ref}
        style={{ ...(props.style ?? {}), minHeight: readOnly ? 0 : props.style?.minHeight }}
      />
    );
  }
);

export const InputNumber = React.forwardRef<HTMLInputElement, InputNumberProps>(
  (props: InputNumberProps, ref: React.ForwardedRef<HTMLInputElement>): ReactElement => {
    const readOnly = useContext(ReadOnlyContext);
    return <AntdInputNumber {...props} readOnly={readOnly ?? props.readOnly} ref={ref} />;
  }
);

export function ThreatIndexInputNumber(props: InputNumberProps): ReactElement {
  return <InputNumber {...props} max={10} min={0} precision={1} step={0.1} />;
}

// Moved placeholder to input where possible so that it is affected by input style
export function AutoComplete(
  props: Omit<AutoCompleteProps, 'children'> & { inputStyle?: InputProps['style'] }
): ReactElement {
  const { inputStyle, placeholder, ...otherProps } = props;
  const readOnly = useContext(ReadOnlyContext);
  if (readOnly)
    return (
      <Input
        value={props.value}
        readOnly
        style={inputStyle}
        placeholder={typeof placeholder === 'string' ? placeholder : undefined}
      />
    );
  return (
    <AntdAutoComplete
      {...otherProps}
      placeholder={typeof placeholder === 'string' ? undefined : placeholder}
      children={
        <Input
          style={inputStyle}
          placeholder={typeof placeholder === 'string' ? placeholder : undefined}
        />
      }
    />
  );
}

function Checkbox(props: CheckboxProps): ReactElement {
  const readOnly = useContext(ReadOnlyContext);
  return <AntdCheckbox {...props} disabled={readOnly || props.disabled} />;
}
Checkbox.Group = function CheckboxGroup(props: CheckboxGroupProps): ReactElement {
  const readOnly = useContext(ReadOnlyContext);
  return (
    <AntdCheckbox.Group
      {...props}
      disabled={readOnly ?? props.disabled}
      // Custom styles to make them list vertically, I never want them to wrap
      style={{ ...props.style, display: 'flex', flexDirection: 'column', marginLeft: 10 }}
    />
  );
};
export { Checkbox };

// Customize date format, otherwise typing in year adds 0's before you can finish typing the year because of how date-fns parse function works
const pickerDateFormat = dateFormat.replace(/Y+/, 'y');
const timePickerFormat = 'h:mm';
const betterLocale: typeof en = {
  ...en,
  lang: {
    ...en.lang,
    cellMeridiemFormat: 'a'
  }
};
const MyDatePicker = AntdDefaultDatePicker.generatePicker<Date>(dateFnsGenerateConfig);
export function DatePicker(props: PickerProps): ReactElement {
  const { format, ...otherProps } = props;

  return <MyDatePicker {...otherProps} locale={betterLocale} format={format ?? pickerDateFormat} />;
}

const MyTimePicker = generatePicker<Date>(dateFnsGenerateConfig);
export function TimePicker(props: PickerProps): ReactElement {
  const { format, ...otherProps } = props;
  return (
    <MyTimePicker
      {...otherProps}
      locale={betterLocale}
      format={format ?? timePickerFormat}
      picker="time"
    />
  );
}

export type SelectProps<T extends LabeledValue> = AntdSelectProps<T | string> & {
  readOnly?: boolean;
};
function Select<T extends LabeledValue>(props: SelectProps<T>): ReactElement {
  const readOnlyContext = useContext(ReadOnlyContext);
  const readOnly = props.readOnly || readOnlyContext;
  const { open, style, ...otherProps } = props;

  return (
    <AntdSelect<T | string>
      {...otherProps}
      style={readOnly ? { ...style, cursor: 'not-allowed' } : style}
      open={readOnly ? false : open}
      {...(readOnly ? { suffixIcon: null } : {})}
      getPopupContainer={() => document.body}
    />
  );
}
Select.Option = AntdSelect.Option;
Select.OptGroup = AntdSelect.OptGroup;
export { Select };

export function TreeSelect<T extends any[]>(props: TreeSelectProps<T>): ReactElement {
  const readOnly = useContext(ReadOnlyContext);
  const { open, style, ...otherProps } = props;
  return (
    <AntdTreeSelect<T>
      {...otherProps}
      style={readOnly ? { ...style, cursor: 'not-allowed' } : style}
      open={readOnly ? false : open}
      {...(readOnly ? { suffixIcon: null } : {})}
    />
  );
}

function Radio(props: RadioProps): ReactElement {
  const readOnly = useContext(ReadOnlyContext);
  return <AntdRadio {...props} disabled={props.disabled ?? readOnly} />;
}
Radio.Group = AntdRadio.Group;
Radio.Button = AntdRadio.Button;
export { Radio };

type AutosizeInputProps = {
  value?: string;
  onChange?: (e: InputEvent) => void;
  minWidth?: number;
  block?: boolean;
  className?: string;
  placeholder?: string;
};
export const AutosizeInput = React.forwardRef<HTMLSpanElement, AutosizeInputProps>(
  (props: AutosizeInputProps, ref: React.ForwardedRef<HTMLSpanElement>): ReactElement => {
    const { value, onChange, block, minWidth, className, placeholder } = props;
    const readOnly = useContext(ReadOnlyContext);
    const spanRef = useRef<HTMLSpanElement>(null) as MutableRefObject<HTMLSpanElement>;

    useEffect(() => {
      if (spanRef.current && spanRef.current.textContent !== value)
        spanRef.current.textContent = value ?? null;
    }, [value, spanRef]);

    return (
      <span
        ref={r => {
          if (ref) {
            if (typeof ref === 'function') ref(r);
            else ref.current = r;
          }
          if (r) {
            spanRef.current = r;
          }
        }}
        //placeholder={placeholder}  TODO: Do we need this placeholder
        contentEditable={!readOnly}
        className={'ant-input autosize-input' + (className ? ' ' + className : '')}
        style={{
          display: block ? 'inline-block' : 'inline',
          minWidth: minWidth ?? 100,
          lineHeight: readOnly ? undefined : '28px'
        }}
        // Prevent enter from doing anything, like a normal input
        onKeyPress={event => event.key === 'Enter' && event.preventDefault()}
        onInput={e =>
          !readOnly &&
          onChange &&
          onChange({
            ...e,
            target: { ...e.target, value: (e.target as HTMLElement).textContent }
          } as unknown as InputEvent)
        }
      />
    );
  }
);

export const FormContext = React.createContext<FormInstance | undefined>(undefined);
export type TranslatableFormItemProps<T> = Omit<FormItemProps<T>, 'label'> & {
  labelKey?: string;
  noLabel?: boolean;
};
interface OmitFormProps<Values = any> extends Omit<FormProps<Values>, 'children'> {
  children?: React.ReactNode;
}

function Form<Values = Record<string, unknown>>(props: OmitFormProps<Values>): ReactElement {
  const [form] = AntdForm.useForm<Values>(props.form);
  return (
    <FormContext.Provider value={form}>
      <AntdForm form={form} {...props} />
    </FormContext.Provider>
  );
}
function FormItem<Values = unknown>(props: TranslatableFormItemProps<Values>): ReactElement {
  const { name, labelKey, noLabel, ...otherProps } = props;
  const { t } = useTranslation();
  const actualLabelKey = labelKey ?? (typeof name === 'string' ? name : null);
  return (
    <AntdForm.Item
      {...otherProps}
      name={name}
      label={noLabel || !actualLabelKey ? undefined : t('labels.' + actualLabelKey, actualLabelKey)}
    />
  );
}
// This will enforce types on the add() function to prevent mistakes
type FormListAddOperation<T> = (defaultValues?: Partial<T>, index?: number) => void;
type FormListPropsTyped<T> = Omit<FormListProps, 'children'> & {
  children: (
    fields: FormListFieldData[],
    operations: Omit<FormListOperation, 'add'> & { add: FormListAddOperation<T> },
    meta: { errors: React.ReactNode[]; warnings: React.ReactNode[] }
  ) => React.ReactNode;
};
function FormList<T = 'No type parameter was supplied'>(props: FormListPropsTyped<NoInfer<T>>) {
  return <AntdForm.List {...props} />;
}
function FormErrorList(props: ErrorListProps) {
  return <AntdForm.ErrorList {...props} />;
}
Form.Item = FormItem;
Form.useForm = AntdForm.useForm;
Form.List = FormList;
Form.ErrorList = FormErrorList;

export { Form };

export function JsonValidator(_, value) {
  const errorList: ParseError[] = [];
  parseJson(value, errorList);
  if (value.length === 0 || errorList.length === 0) {
    return Promise.resolve();
  }
  return Promise.reject(
    new Error(
      errorList
        .map(
          e =>
            `${printParseErrorCode(e.error)} 
            at offset ${e.offset} near ${value.toString().substring(e.offset, e.offset + e.length)}`
        )
        .join(', ')
    )
  );
}
